import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { task } from 'ember-concurrency';
import getConfig from '../lib/get-config.js';
import { getDevOrg, computeHost, setDevOrg } from '../lib/dev-org.js';
import { bbParam } from '../lib/utility/ajax.js';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{! Dev-only toolbar — org switch + login (password / paste token). Mai in produzione. }}\n<div class=\'dev-toolbar {{if this.open \"is-open\"}}\'>\n  <button type=\'button\' class=\'dev-toolbar__tab\' data-test-bb-dev-toolbar-toggle {{on \'click\' this.toggle}}>\n    DEV · {{this.org}}\n  </button>\n\n  {{#if this.open}}\n    <div class=\'dev-toolbar__panel\'>\n      <div class=\'dev-toolbar__row\'>\n        <label>\n          Organizzazione o host locale\n        </label>\n        <div class=\'dev-toolbar__inline\'>\n          <input\n            type=\'text\'\n            value={{this.org}}\n            placeholder=\'fisiotest · localhost:3001 · 192.168.1.2:3001\'\n            data-test-update-field\n            {{on \'input\' (fn this.updateField \'org\')}}\n          />\n          <button type=\'button\' data-test-switch-org {{on \'click\' this.switchOrg}}>\n            Cambia + reload\n          </button>\n        </div>\n        <small>\n          Host: {{this.currentHost}}\n        </small>\n      </div>\n\n      <hr />\n\n      <div class=\'dev-toolbar__row\'>\n        <label>\n          Login utente\n        </label>\n        <input\n          type=\'text\'\n          value={{this.username}}\n          placeholder=\'username / email\'\n          autocomplete=\'username\'\n          data-test-update-field-2\n          {{on \'input\' (fn this.updateField \'username\')}}\n        />\n        <input\n          type=\'password\'\n          value={{this.password}}\n          placeholder=\'password\'\n          autocomplete=\'current-password\'\n          data-test-update-field-3\n          {{on \'input\' (fn this.updateField \'password\')}}\n        />\n        <button\n          type=\'button\'\n          disabled={{this.loginTask.isRunning}}\n          data-test-login\n          {{on \'click\' (perform this.loginTask)}}\n        >\n          {{if this.loginTask.isRunning \'Accesso…\' \'Login\'}}\n        </button>\n      </div>\n\n      <hr />\n\n      <div class=\'dev-toolbar__row\'>\n        <label>\n          Oppure incolla access_token\n        </label>\n        {{! The value MUST stay on one line: a <textarea> keeps its literal\n        element content, so the pretty-printed block form seeded the box with\n        the template\'s own indentation and the field looked pre-filled. }}\n        <textarea\n          rows=\'2\'\n          placeholder=\'access_token\'\n          data-test-update-field-4\n          {{on \'input\' (fn this.updateField \'token\')}}\n        >{{this.token}}</textarea>\n        <button\n          type=\'button\'\n          disabled={{this.tokenLoginTask.isRunning}}\n          data-test-token-login\n          {{on \'click\' (perform this.tokenLoginTask)}}\n        >\n          Login con token\n        </button>\n      </div>\n\n      {{#if this.error}}\n        <p class=\'dev-toolbar__error\'>\n          {{if (eq this.error \'token\') \'Token mancante\' \'Credenziali non valide\'}}\n        </p>\n      {{/if}}\n    </div>\n  {{/if}}\n</div>");

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7;
let BbDevToolbarComponent = (_class = class BbDevToolbarComponent extends Component {
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "session", _descriptor, this);
    _initializerDefineProperty(this, "open", _descriptor2, this);
    _initializerDefineProperty(this, "org", _descriptor3, this);
    _initializerDefineProperty(this, "username", _descriptor4, this);
    _initializerDefineProperty(this, "password", _descriptor5, this);
    _initializerDefineProperty(this, "token", _descriptor6, this);
    _initializerDefineProperty(this, "error", _descriptor7, this);
    if (this.args.autoOpen) {
      this.open = true;
    }
  }

  /** Valore dedotto dall'host corrente in config (fallback se localStorage vuoto) */
  get currentOrgFromHost() {
    const host = getConfig(this).APP?.host || '';
    const slug = host.match(/https?:\/\/([^.]+)\.beebeeboard\.com/);
    if (slug) {
      return slug[1];
    }
    // host locale/IP: ritorna "host:port" (senza protocollo né /api)
    const local = host.match(/https?:\/\/([^/]+)/);
    return local ? local[1] : '';
  }

  /** Host API per l'org attualmente selezionata in localStorage */
  get currentHost() {
    const org = getDevOrg();
    return org ? computeHost(org) : getConfig(this).APP?.host;
  }
  toggle() {
    this.open = !this.open;
  }
  updateField(field, event) {
    this[field] = event.target.value;
  }

  /**
   * Salva la nuova org e ricarica: l'host viene riapplicato al boot.
   * Cancella il token corrente (cookie `secure`) PRIMA del reload: altrimenti
   * il nuovo host partirebbe con un GET contacts/me usando il token della
   * vecchia org → 401 / dati errati.
   */
  switchOrg() {
    const org = (this.org || '').trim();
    if (!org) {
      return;
    }
    setDevOrg(org);
    this.session.logout();
    window.location.reload();
  }

  /** Login username/password → access_token → session.afterLoginTask */
  *loginTask() {
    this.error = null;
    const data = {
      grant_type: 'password',
      username: this.username ? this.username.trim() : null,
      password: this.password ? this.password.trim() : null
    };
    try {
      const response = yield fetch(`${getConfig(this).APP.urlApi}/../oauth/token`, {
        method: 'POST',
        headers: {
          Authorization: 'Basic YWJjMTIzOnNzaC1zZWNyZXQ=',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        body: bbParam(data)
      });
      if (!response.ok) {
        throw new Error(`loginTask: HTTP ${response.status}`);
      }
      const accesso = yield response.json();
      if (accesso.access_token) {
        this.session.showDevLogin = false;
        this.session.afterLoginTask.perform(accesso.access_token);
      } else {
        this.error = 'login';
      }
    } catch (e) {
      this.error = 'login';
    }
  }

  /** Login incollando un access_token già ottenuto */
  *tokenLoginTask() {
    this.error = null;
    const token = (this.token || '').trim();
    if (!token) {
      this.error = 'token';
      return;
    }
    this.session.showDevLogin = false;
    this.session.afterLoginTask.perform(token);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "open", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "org", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return getDevOrg() || this.currentOrgFromHost;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "username", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "password", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "token", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "error", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "toggle", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggle"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateField", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateField"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "switchOrg", [action], Object.getOwnPropertyDescriptor(_class.prototype, "switchOrg"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "loginTask", [task], Object.getOwnPropertyDescriptor(_class.prototype, "loginTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "tokenLoginTask", [task], Object.getOwnPropertyDescriptor(_class.prototype, "tokenLoginTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbDevToolbarComponent);

export { BbDevToolbarComponent as default };
//# sourceMappingURL=bb-dev-toolbar.js.map
